import { z } from 'zod';

import { emailSchema, idSchema, instantSchema, timezoneSchema } from './common';
import {
  labelCategorySchema,
  labelKindSchema,
  phonePlatformSchema,
  userRoleSchema,
  userStatusSchema,
} from './enums';
import { LIMITS } from './limits';

/** Request and response contracts of the identity area (api-design §3.2, §3.3). */

const personNameSchema = z.string().trim().min(1).max(LIMITS.userNameMax);

/** New passwords follow AUTH-5; the breach check happens on the server. */
export const newPasswordSchema = z.string().min(LIMITS.passwordMin).max(LIMITS.passwordMax);

/** Opaque token from an emailed link (32 random bytes, base64url). */
export const emailTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'Invalid link.');

// ── Auth ─────────────────────────────────────────────────────────────────────────────────────────

export const loginRequestSchema = z.object({
  email: emailSchema,
  // Existing passwords are only compared, never re-validated against today's policy.
  password: z.string().min(1).max(LIMITS.passwordMax),
});

export const forgotPasswordRequestSchema = z.object({ email: emailSchema });

export const resetPasswordRequestSchema = z.object({
  token: emailTokenSchema,
  password: newPasswordSchema,
});

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(LIMITS.passwordMax),
  newPassword: newPasswordSchema,
});

// ── People ───────────────────────────────────────────────────────────────────────────────────────

/** The signed-in user as they see themselves. */
export const meSchema = z.object({
  id: idSchema,
  email: z.string(),
  name: z.string(),
  role: userRoleSchema,
  timezone: z.string(),
});

/** Another person as shown in pickers and avatars — no email (engineering standards §8). */
export const personSchema = z.object({
  id: idSchema,
  name: z.string(),
  role: userRoleSchema,
  status: userStatusSchema,
});

export const peopleResponseSchema = z.object({ items: z.array(personSchema) });

export const updateMeRequestSchema = z
  .object({ name: personNameSchema.optional(), timezone: timezoneSchema.optional() })
  .refine((value) => value.name !== undefined || value.timezone !== undefined, {
    message: 'Change at least one field.',
  });

// ── Bootstrap ────────────────────────────────────────────────────────────────────────────────────

export const labelSchema = z.object({
  id: idSchema,
  kind: labelKindSchema,
  name: z.string(),
  color: z.string(),
  category: labelCategorySchema.nullable(),
  position: z.number().int(),
  isDefault: z.boolean(),
});

/** `GET /auth/session`: everything the web app needs right after sign-in. */
export const sessionResponseSchema = z.object({
  user: meSchema,
  org: z.object({ name: z.string(), taskKeyPrefix: z.string() }),
  labels: z.array(labelSchema),
  /**
   * D41, D45: set while an Admin or Manager is signed in as `user`: their ID and name, for the banner
   * and its exit. Null otherwise.
   */
  viewedBy: z.object({ id: idSchema, name: z.string() }).nullable(),
});

/** D45: sign in as this person (an Admin: anyone active; a Manager: someone in their team). */
export const startViewAsRequestSchema = z.object({ userId: idSchema });

// ── Phones (D46, ADR-0014) ───────────────────────────────────────────────────────────────────────

/** What the app tells the server about the phone it runs on. */
export const phoneDeviceSchema = z.object({
  name: z.string().trim().min(1).max(LIMITS.phoneNameMax),
  platform: phonePlatformSchema,
  appVersion: z.string().trim().min(1).max(LIMITS.appVersionMax),
});

export const phoneSignInRequestSchema = loginRequestSchema.extend({ device: phoneDeviceSchema });

/** A phone signed in to the app, as Connected phones lists it. */
export const phoneSchema = z.object({
  id: idSchema,
  name: z.string(),
  platform: phonePlatformSchema,
  appVersion: z.string(),
  connectedAt: instantSchema,
  lastSeenAt: instantSchema,
  /** The phone this request comes from. */
  isCurrent: z.boolean(),
});

export const phonesResponseSchema = z.object({ items: z.array(phoneSchema) });

/**
 * `POST /auth/phones`: the token the app keeps in the phone's secure storage (sent as
 * `Authorization: Bearer`), the phone, and the same bootstrap as `GET /auth/session`.
 */
export const phoneSignInResponseSchema = z.object({
  token: z.string(),
  phone: phoneSchema,
  session: sessionResponseSchema,
});

// ── Invitations ──────────────────────────────────────────────────────────────────────────────────

export const createInvitationRequestSchema = z.object({
  email: emailSchema,
  role: userRoleSchema,
  name: personNameSchema.optional(),
});

export const invitationSchema = z.object({
  id: idSchema,
  email: z.string(),
  name: z.string().nullable(),
  role: userRoleSchema,
  invitedBy: z.object({ id: idSchema, name: z.string() }),
  createdAt: instantSchema,
  expiresAt: instantSchema,
  sentAt: instantSchema.nullable(),
  isExpired: z.boolean(),
});

export const invitationsResponseSchema = z.object({ items: z.array(invitationSchema) });

export const invitationPreviewSchema = z.object({
  email: z.string(),
  name: z.string().nullable(),
  role: userRoleSchema,
  invitedByName: z.string(),
  expiresAt: instantSchema,
});

export const acceptInvitationRequestSchema = z.object({
  name: personNameSchema,
  password: newPasswordSchema,
});

// ── Administration ───────────────────────────────────────────────────────────────────────────────

export const adminUserSchema = z.object({
  id: idSchema,
  email: z.string(),
  name: z.string(),
  role: userRoleSchema,
  status: userStatusSchema,
  timezone: z.string(),
  lastSeenAt: instantSchema.nullable(),
  createdAt: instantSchema,
  deactivatedAt: instantSchema.nullable(),
  /** D42: a demo person for training; goes when the demo data is removed. */
  demo: z.boolean(),
  /** D45: a Manager's team, the people they may sign in as. Empty for everyone else. */
  team: z.array(idSchema),
});

export const adminUsersResponseSchema = z.object({ items: z.array(adminUserSchema) });

/** D42: how much demo data is loaded for training (or was just removed). */
export const demoDataSchema = z.object({
  projects: z.number().int().min(0),
  people: z.number().int().min(0),
});

export const updateUserRequestSchema = z
  .object({ name: personNameSchema.optional(), role: userRoleSchema.optional() })
  .refine((value) => value.name !== undefined || value.role !== undefined, {
    message: 'Change at least one field.',
  });

/** D45: who is in a Manager's team (replaces the team). */
export const updateTeamRequestSchema = z.object({
  memberIds: z
    .array(idSchema)
    .max(LIMITS.teamMembersMax)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'List each person once.' }),
});

export const idParamsSchema = z.object({ id: idSchema });
export const tokenParamsSchema = z.object({ token: emailTokenSchema });
export const userPhoneParamsSchema = z.object({ id: idSchema, phoneId: idSchema });

/**
 * ADM-3: deactivate someone and say who takes over which of their open work items. Items not listed
 * keep them as an owner.
 */
export const deactivateUserRequestSchema = z.object({
  reassignments: z
    .array(z.object({ taskId: idSchema, ownerId: idSchema }))
    .max(LIMITS.reassignmentsMax)
    .refine((list) => new Set(list.map((r) => r.taskId)).size === list.length, {
      message: 'Reassign each work item once.',
    }),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;
export type Me = z.infer<typeof meSchema>;
export type Person = z.infer<typeof personSchema>;
export type PeopleResponse = z.infer<typeof peopleResponseSchema>;
export type Label = z.infer<typeof labelSchema>;
export type SessionResponse = z.infer<typeof sessionResponseSchema>;
export type Invitation = z.infer<typeof invitationSchema>;
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;
export type AdminUser = z.infer<typeof adminUserSchema>;
export type DemoData = z.infer<typeof demoDataSchema>;
export type DeactivateUserRequest = z.infer<typeof deactivateUserRequestSchema>;
export type UpdateTeamRequest = z.infer<typeof updateTeamRequestSchema>;
export type PhoneDevice = z.infer<typeof phoneDeviceSchema>;
export type PhoneSignInRequest = z.infer<typeof phoneSignInRequestSchema>;
export type PhoneSignInResponse = z.infer<typeof phoneSignInResponseSchema>;
export type Phone = z.infer<typeof phoneSchema>;
export type PhonesResponse = z.infer<typeof phonesResponseSchema>;
